/**
 * MyChatbots - Dashboard to manage user's chatbots
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  Bot, Plus, Trash2, ExternalLink, Code, Copy, 
  CheckCircle, Loader2, MessageSquare, FileText 
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyChatbots() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/auth?redirect=/my-chatbots');
      return;
    }
    fetchChatbots();
  }, [user, navigate]);

  const fetchChatbots = async () => {
    try {
      const response = await axios.get(`${API}/chatbots/my-chatbots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatbots(response.data);
    } catch (err) {
      console.error('Failed to fetch chatbots:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (chatbotId) => {
    if (!confirm('Are you sure you want to delete this chatbot?')) return;
    
    setDeleting(chatbotId);
    try {
      await axios.delete(`${API}/chatbots/${chatbotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatbots(chatbots.filter(c => c.chatbot_id !== chatbotId));
    } catch (err) {
      alert('Failed to delete chatbot');
    } finally {
      setDeleting(null);
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              My Chatbots
            </h1>
            <p className="text-zinc-400 mt-1">
              Manage your AI chatbots
            </p>
          </div>
          <Button
            onClick={() => navigate('/chatbot-builder')}
            className="bg-gradient-to-r from-teal-500 to-blue-500 hover:from-teal-400 hover:to-blue-400"
            data-testid="create-new-chatbot-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New
          </Button>
        </div>

        {/* Chatbots List */}
        {chatbots.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-white/10 rounded-2xl p-12 text-center"
          >
            <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <Bot className="w-8 h-8 text-zinc-500" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              No chatbots yet
            </h2>
            <p className="text-zinc-400 mb-6">
              Create your first AI chatbot by uploading a document.
            </p>
            <Button
              onClick={() => navigate('/chatbot-builder')}
              className="bg-gradient-to-r from-teal-500 to-blue-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Chatbot
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {chatbots.map((bot, index) => (
              <motion.div
                key={bot.chatbot_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-zinc-900/50 border border-white/10 rounded-xl p-6 hover:border-teal-500/30 transition-all"
                data-testid={`chatbot-card-${bot.public_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500/20 to-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Bot className="w-6 h-6 text-teal-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">
                        {bot.name}
                      </h3>
                      {bot.description && (
                        <p className="text-zinc-400 text-sm mt-1">
                          {bot.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-sm text-zinc-500">
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {bot.filename}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-4 h-4" />
                          {bot.chunk_count} chunks
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/chat/${bot.public_id}`)}
                      className="border-zinc-700 text-zinc-300 hover:text-white"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      Open
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(bot.chatbot_id)}
                      disabled={deleting === bot.chatbot_id}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      {deleting === bot.chatbot_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(
                      `${window.location.origin}/chat/${bot.public_id}`,
                      `link-${bot.public_id}`
                    )}
                    className="text-zinc-400 hover:text-white text-xs"
                  >
                    {copied === `link-${bot.public_id}` ? (
                      <CheckCircle className="w-3 h-3 mr-1 text-green-400" />
                    ) : (
                      <Copy className="w-3 h-3 mr-1" />
                    )}
                    Copy Link
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(
                      `<iframe src="${window.location.origin}/api/chatbots/embed/${bot.public_id}" width="400" height="600" frameborder="0"></iframe>`,
                      `embed-${bot.public_id}`
                    )}
                    className="text-zinc-400 hover:text-white text-xs"
                  >
                    {copied === `embed-${bot.public_id}` ? (
                      <CheckCircle className="w-3 h-3 mr-1 text-green-400" />
                    ) : (
                      <Code className="w-3 h-3 mr-1" />
                    )}
                    Copy Embed Code
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Limit Notice */}
        {!user?.is_subscriber && chatbots.length >= 1 && (
          <div className="mt-6 bg-gradient-to-r from-teal-500/10 to-blue-500/10 border border-teal-500/20 rounded-xl p-4 text-center">
            <p className="text-zinc-300">
              You've reached the free limit (1 chatbot).{' '}
              <button
                onClick={() => navigate('/subscription')}
                className="text-teal-400 hover:text-teal-300 font-medium"
              >
                Upgrade to Premium
              </button>
              {' '}for unlimited chatbots!
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
